import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

export interface CVERecord {
    id: string;
    cveId: string;
    vendorProject: string;
    product: string;
    name: string;
    shortDescription: string;
    cvss?: number;
    dateAdded?: string;  // YYYY-MM-DD
    dueDate?: string;    // YYYY-MM-DD
    ransomwareUse: boolean;
    cwes: string[];
    severity: number;
    firstSeen: string;
    lastSeen: string;
    tags: string[];
    metadata: Record<string, unknown>;
    /**
     * First-seen snapshot of fields the source produced. Used by the detail
     * dialog to render an "overridden vs. source" diff and to revert a
     * field back to its source value. Undefined keys mean no snapshot was
     * captured for that field yet.
     */
    sourceValues?: {
        severity?: number;
        tags?: string[];
    };
}

export interface CVEListResult {
    cves: CVERecord[];
    total: number;
    limit: number;
    offset: number;
}

export interface CVEFilters {
    search?: string;
    vendor?: string;
    minSeverity?: number;
    ransomwareOnly?: boolean;
    limit?: number;
    offset?: number;
}

const LIST_KEY = (f: CVEFilters) =>
    [
        "admin",
        "cves",
        f.search ?? "",
        f.vendor ?? "",
        f.minSeverity ?? 0,
        f.ransomwareOnly ?? false,
        f.limit ?? 50,
        f.offset ?? 0,
    ] as const;

export function useCVEs(filters: CVEFilters = {}) {
    const limit = filters.limit ?? 50;
    const offset = filters.offset ?? 0;
    return useQuery({
        queryKey: LIST_KEY(filters),
        queryFn: () => {
            const p = new URLSearchParams();
            if (filters.search) p.set("search", filters.search);
            if (filters.vendor) p.set("vendor", filters.vendor);
            if (filters.minSeverity && filters.minSeverity > 1)
                p.set("minSeverity", String(filters.minSeverity));
            if (filters.ransomwareOnly) p.set("ransomware", "true");
            p.set("limit", String(limit));
            p.set("offset", String(offset));
            return api.get<CVEListResult>(`/api/v2/cves?${p.toString()}`);
        },
        staleTime: 10_000,
    });
}

export function useCVE(id: string | null) {
    return useQuery({
        queryKey: ["admin", "cves", "detail", id] as const,
        queryFn: () => api.get<{ cve: CVERecord }>(`/api/v2/cves/${id}`).then((d) => d.cve),
        enabled: !!id,
        staleTime: 10_000,
    });
}

interface UpdateCVEInput {
    id: string;
    severity?: number;
    tags?: string[];
}

export function useUpdateCVE() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...patch }: UpdateCVEInput) =>
            api.patch<{ cve: CVERecord }>(`/api/v2/cves/${id}`, patch),
        onSuccess: (_data, variables) => {
            void qc.invalidateQueries({ queryKey: ["admin", "cves"] });
            void qc.invalidateQueries({ queryKey: ["admin", "cves", "detail", variables.id] });
        },
    });
}

interface RevertCVEInput {
    id: string;
    /** subset of "severity" | "tags" */
    fields: string[];
}

/** Resets the named fields to the source-captured values and clears any
 * overridden flags. The server returns the refreshed row. */
export function useRevertCVE() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, fields }: RevertCVEInput) =>
            api.post<{ cve: CVERecord }>(`/api/v2/cves/${id}/revert`, { fields }),
        onSuccess: (_data, variables) => {
            void qc.invalidateQueries({ queryKey: ["admin", "cves"] });
            void qc.invalidateQueries({ queryKey: ["admin", "cves", "detail", variables.id] });
        },
    });
}
